import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CopyIcon,
  IdCardIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
} from '@/shared/ui/icons';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiCargoIdDelete, useCargoIds } from '@/entities/cargo-id';
import { FeaturesCargoIdDebtRow } from './DebtRow';

const INVALIDATE = [queryKeys.cargoIds.all, queryKeys.parcels.all];

export const FeaturesCargoIdProfileCargoIdBanner = () => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError } = useCargoIds();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useMutate({
    api: (id: string) => apiCargoIdDelete(id),
    invalidateKeys: INVALIDATE,
    onSuccess: () => {
      toast.success(t('toast.deleted'));
      setDeleteOpen(false);
    },
    onError: (_e, message) => toast.error(message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50 p-4">
        <Loader2Icon className="h-5 w-5 shrink-0 animate-spin text-brand-600" aria-hidden />
        <div className="flex flex-col gap-2">
          <span className="h-3 w-24 animate-pulse rounded bg-brand-100" />
          <span className="h-5 w-40 animate-pulse rounded bg-brand-100" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t('profileBanner.label')}
        </p>
        <p className="mt-1 text-sm text-ink-muted">{t('loadError')}</p>
      </div>
    );
  }

  // No Cargo ID at all → CTA
  if (!data) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
            <IdCardIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {t('profileBanner.label')}
            </p>
            <p className="truncate text-sm font-semibold text-ink">
              {t('profileBanner.noneTitle')}
            </p>
          </div>
        </div>
        <p className="text-xs text-ink-muted">{t('profileBanner.noneHelper')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="sm"
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => navigate('/ids/request')}
            className="w-full sm:w-auto"
          >
            {t('profileBanner.getCta')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/ids/connect')}
            className="w-full sm:w-auto"
          >
            {t('profileBanner.connectCta')}
          </Button>
        </div>
      </div>
    );
  }

  const code = data.code ?? '';

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('profileBanner.copied'));
    } catch {
      toast.error(t('profileBanner.copyFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-brand-300 bg-gradient-to-br from-brand-50 to-white p-4 shadow-card ring-1 ring-brand-200">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
          <IdCardIcon className="h-5 w-5" aria-hidden />
        </span>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t('profileBanner.label')}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate font-mono text-2xl font-bold tracking-wide text-brand-800">
          {code}
        </p>
        <IconButton
          label={t('profileBanner.copy')}
          variant="ghost"
          size="sm"
          onClick={() => void onCopy()}
        >
          <CopyIcon className="h-5 w-5 text-brand-600" />
        </IconButton>
        <IconButton
          label={t('actions.unbind')}
          variant="ghost"
          size="sm"
          disabled={remove.isPending}
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2Icon className="h-5 w-5 text-red-600" />
        </IconButton>
      </div>

      {(data.full_name || data.phone_number) && (
        <div className="flex flex-col gap-0.5 text-sm text-ink">
          {data.full_name && <p className="font-medium">{data.full_name}</p>}
          {data.phone_number && (
            <p className="text-xs text-ink-muted">{data.phone_number}</p>
          )}
        </div>
      )}

      <FeaturesCargoIdDebtRow />

      <p className="text-xs text-ink-muted">{t('profileBanner.helper')}</p>

      <ConfirmDialog
        open={deleteOpen}
        title={t('confirmUnbindTitle')}
        description={t('confirmUnbindDescription')}
        confirmLabel={t('actions.unbind')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => {
          if (!remove.isPending) setDeleteOpen(false);
        }}
        onConfirm={() => remove.mutate(data.id)}
      />
    </div>
  );
};
